import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { Button } from "../components/ui/button";
import { BRAND, SEGMENTS, HUB_TILES } from "../lib/data";

const Icon = ({ name, className }) => { const C = Icons[name] || Icons.Circle; return <C className={className} />; };
const fadeUp = { hidden: { opacity: 0, y: 24 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.45, delay: i * 0.05 } }) };
const Overline = ({ children }) => <div className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta-600 mb-3">{children}</div>;

export default function Mall() {
  return (
    <div data-testid="mall-page" className="bg-cream-100 text-soil-700 pt-24 pb-24">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 pb-12">
        <div className="relative rounded-3xl overflow-hidden forest-gradient text-cream-50 p-8 sm:p-14 text-center">
          <div className="absolute inset-0 bharat-pattern opacity-40" />
          <div className="relative">
            <div className="inline-block text-xs uppercase tracking-[0.22em] font-bold text-saffron-500 mb-3">{BRAND.mallFull}</div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">GENESIS <span className="text-saffron-500">MALL</span></h1>
            <p className="mt-2 text-lg font-semibold text-cream-100">{BRAND.businessCenter}</p>
            <p className="mt-3 text-cream-200/90 max-w-2xl mx-auto">Where business, services, shopping and opportunities come together — connecting Bharat.</p>
            <div className="mt-8 max-w-xl mx-auto flex items-center gap-2 bg-cream-50 rounded-full px-4 h-12" data-testid="mall-search">
              <Icons.Search className="w-5 h-5 text-forest-600 shrink-0" />
              <input placeholder="Search businesses, services & products…" className="flex-1 bg-transparent text-soil-700 placeholder:text-soil-400 text-sm focus:outline-none" data-testid="mall-search-input" />
              <Button className="terracotta-gradient text-cream-50 font-bold rounded-full h-9 px-5" data-testid="mall-search-button">Search</Button>
            </div>
          </div>
        </div>
      </section>

      {/* ALL-IN-ONE HUB */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-6">
        <div className="text-center"><Overline>All-in-One Business Center</Overline><h2 className="font-heading text-2xl sm:text-3xl font-bold text-forest-800">Everything under one ecosystem</h2></div>
        <div className="grid grid-cols-3 lg:grid-cols-9 gap-3 mt-8">
          {HUB_TILES.map((h, i) => (
            <motion.div key={h.t} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="card-cream rounded-2xl p-4 flex flex-col items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-forest-100 flex items-center justify-center"><Icon name={h.icon} className="w-5 h-5 text-forest-600" /></div>
              <div className="text-xs font-semibold text-soil-700">{h.t}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* EXPLORE BUSINESSES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="text-center"><Overline>Explore Our Businesses</Overline><h2 className="font-heading text-2xl sm:text-3xl font-bold text-forest-800">Genesis Categories</h2></div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-8">
          {SEGMENTS.map((s, i) => (
            <motion.div key={s.slug} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
              <Link to={`/businesses/${s.slug}`} data-testid={`mall-category-${s.slug}`} className="block card-cream rounded-2xl p-5 hover:-translate-y-1 hover:shadow-lg transition-all h-full">
                <div className="w-11 h-11 rounded-xl bg-terracotta-100 flex items-center justify-center mb-3"><Icon name={s.icon} className="w-5 h-5 text-terracotta-600" /></div>
                <div className="font-heading font-semibold text-forest-800 text-sm">{s.short}</div>
                <div className="text-soil-500 text-xs mt-1 line-clamp-2">{s.tagline}</div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SHOP EMPTY STATE */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="card-cream rounded-3xl p-10 sm:p-14 text-center" data-testid="mall-empty-state">
          <div className="w-16 h-16 mx-auto rounded-2xl forest-gradient flex items-center justify-center mb-5"><Icons.ShoppingBag className="w-8 h-8 text-cream-50" /></div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-forest-800">Genesis Mall is getting ready for you</h2>
          <p className="text-soil-600 mt-3 max-w-xl mx-auto">We're onboarding vendors and curating our catalog. Products, services and offers will appear here soon — only the real thing, no placeholders.</p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <Link to="/register" data-testid="mall-shopper-cta"><Button className="forest-gradient text-cream-50 font-bold h-11 px-6 rounded-full">Create an Account</Button></Link>
            <Link to="/opportunity" data-testid="mall-affiliate-cta"><Button className="terracotta-gradient text-cream-50 font-bold h-11 px-6 rounded-full">Earn as an Affiliate</Button></Link>
          </div>
        </div>
      </section>

      {/* WHY / RURAL / JOIN */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8 grid sm:grid-cols-3 gap-5">
        {[
          { icon: "ShieldCheck", t: "Trusted Ecosystem", d: "One professional platform bringing many businesses together." },
          { icon: "Sprout", t: "Local & Rural Connect", d: "Rooted in Bharat — linking local enterprise to wider markets." },
          { icon: "TrendingUp", t: "Opportunity & Growth", d: "Shop, partner or build a network within Genesis." },
        ].map((c) => (
          <div key={c.t} className="card-cream rounded-2xl p-6">
            <div className="w-10 h-10 rounded-xl bg-forest-100 flex items-center justify-center mb-3"><Icon name={c.icon} className="w-5 h-5 text-forest-600" /></div>
            <div className="font-heading font-bold text-forest-800">{c.t}</div>
            <div className="text-soil-500 text-sm mt-1">{c.d}</div>
          </div>
        ))}
      </section>
    </div>
  );
}
